// Тексты главной «карта с пинами» (ТЗ владельца 25.10.2026) на трёх языках.
// AZ — дословно из ТЗ, RU/EN — перевод, на утверждение владельца до мержа.
// AZ — на вычитку носителем.
//
// Число стран в пине «за рубежом» — из справочника (activeCountryCount,
// edu_countries.is_active), не цифрой: см. src/edu/countries-count.ts.
//
// Без серверных импортов: модуль читают HomePins.tsx, серверная функция
// листа ожидания (textbook-waitlist.functions.ts) и scripts/verify-home-pins.mts.
import type { HomeLang } from "./HomePage";

export type PinId = "career" | "tutors" | "abroad" | "textbooks" | "tutor_join";

/** Только азербайджанский мобильный: поле листа ожидания — с приставкой +994. */
export const WAITLIST_PHONE_RE = /^\+994\d{9}$/;

const RU_PLURAL = new Intl.PluralRules("ru");
const EN_PLURAL = new Intl.PluralRules("en");

/** «{N} ölkədə / в {N} странах / in {N} countries»; числа нет — фраза без числа. */
export function abroadPinLine(lang: HomeLang, n: number | null | undefined): string {
  const ok = typeof n === "number" && Number.isInteger(n) && n > 0;
  if (lang === "az") return ok ? `${n} ölkədə universitet seçimi` : "Müxtəlif ölkələrdə universitet seçimi";
  if (lang === "ru") return ok ? `Выбор университета в ${n} ${RU_PLURAL.select(n) === "one" ? "стране" : "странах"}` : "Выбор университета в разных странах";
  return ok ? `University choice in ${n} ${EN_PLURAL.select(n) === "one" ? "country" : "countries"}` : "University choice across many countries";
}

type PinCopy = { title: string; line: string; tag: string };

export type PinsCopy = {
  /** Слоган — подзаголовок крупным шрифтом (до 29.10.2026 был H1). */
  slogan: string;
  sloganTail: string;
  /** H1 со словами поиска (29.10.2026), выглядит как прежняя строка под слоганом. */
  h1: string;
  ask: string;
  pins: Record<Exclude<PinId, "abroad">, PinCopy> & { abroad: Omit<PinCopy, "line"> };
  more: string;
  wait: {
    title: string;
    phoneLabel: string;
    placeholder: string;
    submit: string;
    sending: string;
    done: string;
    close: string;
    errPhone: string;
    errRate: string;
    errFail: string;
  };
};

export const PINS_COPY: Record<HomeLang, PinsCopy> = {
  az: {
    slogan: "Bir yol.",
    sloganTail: "Sonsuz imkanlar.",
    h1: "Karyera testi, repetitorlar və xaricdə təhsil — bir yerdə",
    ask: "Nədən başlamaq istəyirsiniz? Pini seçin.",
    pins: {
      career: { title: "Karyera naviqatoru", line: "Uşağın güclü tərəfləri və uyğun peşələr", tag: "~30 dəq · 35 ₼" },
      tutors: { title: "Repetitor tap", line: "Evə yaxın, fənn və rayon üzrə", tag: "Pulsuz" },
      abroad: { title: "Xaricdə təhsil naviqatoru", tag: "Pulsuz" },
      textbooks: { title: "Dərsliklər", line: "Elektron dərs vəsaitləri", tag: "Tezliklə · xəbər ver" },
      tutor_join: { title: "Repetitor qeydiyyatı", line: "Xəritəyə qoşulun", tag: "Pulsuz · komissiyasız" },
    },
    more: "Yolutap nədir?",
    wait: {
      title: "Çıxanda WhatsApp-da xəbər verək",
      phoneLabel: "WhatsApp nömrəsi",
      placeholder: "50 123 45 67",
      submit: "Xəbər ver",
      sending: "Göndərilir…",
      done: "Təşəkkürlər! Dərsliklər çıxanda WhatsApp-da yazacağıq.",
      close: "Bağla",
      errPhone: "Nömrəni yoxlayın: +994 və 9 rəqəm.",
      errRate: "Bu cihazdan bir saatda çox cəhd olub. Bir az sonra yenidən cəhd edin.",
      errFail: "Xəta baş verdi, yenidən cəhd edin.",
    },
  },
  ru: {
    slogan: "Один путь.",
    sloganTail: "Бесконечные возможности.",
    h1: "Карьерный тест, репетиторы и учёба за рубежом — в одном месте",
    ask: "С чего хотите начать? Выберите метку.",
    pins: {
      career: { title: "Карьерный навигатор", line: "Сильные стороны ребёнка и подходящие профессии", tag: "~30 мин · 35 ₼" },
      tutors: { title: "Найти репетитора", line: "Рядом с домом, по предмету и району", tag: "Бесплатно" },
      abroad: { title: "Навигатор учёбы за рубежом", tag: "Бесплатно" },
      textbooks: { title: "Учебники", line: "Электронные учебные пособия", tag: "Скоро · сообщить" },
      tutor_join: { title: "Регистрация репетитора", line: "Присоединяйтесь к карте", tag: "Бесплатно · без комиссии" },
    },
    more: "Что такое Yolutap?",
    wait: {
      title: "Сообщим в WhatsApp, когда выйдут",
      phoneLabel: "Номер WhatsApp",
      placeholder: "50 123 45 67",
      submit: "Сообщить мне",
      sending: "Отправляем…",
      done: "Спасибо! Напишем в WhatsApp, когда учебники выйдут.",
      close: "Закрыть",
      errPhone: "Проверьте номер: +994 и 9 цифр.",
      errRate: "Слишком много попыток с этого устройства за час. Попробуйте позже.",
      errFail: "Не удалось отправить, попробуйте ещё раз.",
    },
  },
  en: {
    slogan: "One path.",
    sloganTail: "Endless possibilities.",
    h1: "Career test, tutors and study abroad — in one place",
    ask: "Where would you like to start? Pick a pin.",
    pins: {
      career: { title: "Career navigator", line: "Your child's strengths and careers that fit", tag: "~30 min · 35 ₼" },
      tutors: { title: "Find a tutor", line: "Near home, by subject and district", tag: "Free" },
      abroad: { title: "Study abroad navigator", tag: "Free" },
      textbooks: { title: "Textbooks", line: "Digital study books", tag: "Soon · notify me" },
      tutor_join: { title: "Tutor sign-up", line: "Join the map", tag: "Free · no commission" },
    },
    more: "What is Yolutap?",
    wait: {
      title: "We'll message you on WhatsApp when they're out",
      phoneLabel: "WhatsApp number",
      placeholder: "50 123 45 67",
      submit: "Notify me",
      sending: "Sending…",
      done: "Thank you! We'll message you on WhatsApp when the textbooks are out.",
      close: "Close",
      errPhone: "Check the number: +994 and 9 digits.",
      errRate: "Too many attempts from this device in the last hour. Please try later.",
      errFail: "Something went wrong, please try again.",
    },
  },
};

// ─── «Yolutap nədir?» под первым экраном ─────────────────────────────────
// Только то, что уже есть в PRODUCT.md: без новых цифр, отзывов и «проверено».
// {countries} — число стран из справочника (withCountries).
export type AboutCopy = { title: string; lead: string; items: { pin: PinId; title: string; text: string }[] };

export const ABOUT_COPY: Record<HomeLang, AboutCopy> = {
  az: {
    title: "Yolutap nədir?",
    lead: "Yolutap valideynlər üçün bir yerdir: uşağın güclü tərəflərini anlamağa, evə yaxın repetitor tapmağa və xaricdə uyğun universiteti seçməyə kömək edir.",
    items: [
      { pin: "career", title: "Karyera naviqatoru", text: "Yaşa uyğun test (təxminən 30 dəqiqə, ara vermək olar) və 10 səhifəlik PDF hesabat: arxetip, güclü tərəflər və üç karyera istiqaməti. 35 ₼." },
      { pin: "tutors", title: "Repetitor tap", text: "Evə yaxın repetitor və dərnəklərin xəritəsi: fənn və rayon üzrə axtarış. Valideyn üçün pulsuz." },
      { pin: "abroad", title: "Xaricdə təhsil naviqatoru", text: "{countries} universitetləri Azərbaycan attestatı, dil imtahanları və ailə büdcəsi ilə yoxlanılır. Pulsuz." },
      { pin: "textbooks", title: "Dərsliklər", text: "Elektron dərs vəsaitləri hazırlanır. Çıxanda WhatsApp-da xəbər veririk." },
      { pin: "tutor_join", title: "Repetitorlar üçün", text: "Repetitor və dərnəklər xəritəyə pulsuz qoşulur." },
    ],
  },
  ru: {
    title: "Что такое Yolutap?",
    lead: "Yolutap — одно место для родителей: понять сильные стороны ребёнка, найти репетитора рядом с домом и выбрать подходящий университет за рубежом.",
    items: [
      { pin: "career", title: "Карьерный навигатор", text: "Тест по возрасту (около 30 минут, можно прерваться) и PDF-отчёт на 10 страниц: архетип, сильные стороны и три карьерных направления. 35 ₼." },
      { pin: "tutors", title: "Найти репетитора", text: "Карта репетиторов и кружков рядом с домом: поиск по предмету и району. Для родителей бесплатно." },
      { pin: "abroad", title: "Навигатор учёбы за рубежом", text: "Вузы {countries} проверяются по азербайджанскому аттестату, языковым экзаменам и бюджету семьи. Бесплатно." },
      { pin: "textbooks", title: "Учебники", text: "Электронные учебные пособия готовятся. Сообщим в WhatsApp, когда выйдут." },
      { pin: "tutor_join", title: "Для репетиторов", text: "Репетиторы и кружки присоединяются к карте бесплатно." },
    ],
  },
  en: {
    title: "What is Yolutap?",
    lead: "Yolutap is one place for parents: understand your child's strengths, find a tutor near home and choose a suitable university abroad.",
    items: [
      { pin: "career", title: "Career navigator", text: "An age-adapted test (about 30 minutes, can pause) and a 10-page PDF report: archetype, strengths and three career paths. 35 ₼." },
      { pin: "tutors", title: "Find a tutor", text: "A map of tutors and clubs near home: search by subject and district. Free for parents." },
      { pin: "abroad", title: "Study abroad navigator", text: "Universities in {countries} checked against an Azerbaijani school certificate, language exams and the family budget. Free." },
      { pin: "textbooks", title: "Textbooks", text: "Digital study books are on the way. We'll message you on WhatsApp when they're out." },
      { pin: "tutor_join", title: "For tutors", text: "Tutors and clubs join the map for free." },
    ],
  },
};
